import crypto, { UUID } from 'crypto';
import { QueryConfigValues, QueryResult, QueryResultRow } from 'pg';
import { format as sqlFormat } from 'sql-formatter';
import { z } from 'zod';
import { logger } from '../../logger/logger.js';
import { RsError } from '../RsError.js';
import { QueryMetadata } from '../eventManager.js';
import { RequesterDetails } from '../types/customExpressTypes.js';
import { questionMarksToOrderedParams, toSqlLiteral } from './PsqlUtils.js';

// The metadata comment lands in pg_stat_activity and trigger payloads — never include full requesterDetails
const DEFAULT_QUERY_METADATA_KEYS = [
	'userId',
	'adminUserId',
	'isSystemUser',
	'role',
	'host',
	'ipAddress',
	'storeId',
	'storeCustomerId',
	'appTokenId',
	'requestId'
];

export abstract class PsqlConnection {
	private static queryMetadataKeys: string[] | 'ALL' = DEFAULT_QUERY_METADATA_KEYS;

	static setQueryMetadataKeys(keys: string[] | 'ALL') {
		PsqlConnection.queryMetadataKeys = keys;
	}

	readonly instanceId: UUID;
	protected constructor(instanceId?: UUID) {
		this.instanceId = instanceId || crypto.randomUUID();
	}

	private buildQueryMetadata(requesterDetails: RequesterDetails | undefined): QueryMetadata {
		if (PsqlConnection.queryMetadataKeys === 'ALL') {
			return { connectionInstanceId: this.instanceId, ...requesterDetails };
		}
		const meta: QueryMetadata = { connectionInstanceId: this.instanceId };
		// Pre-auth middleware (e.g. store/context resolvers) legitimately queries without requesterDetails
		const details = (requesterDetails ?? {}) as unknown as Record<string, unknown>;
		for (const key of PsqlConnection.queryMetadataKeys) {
			if (details[key] !== undefined) meta[key] = details[key];
		}
		return meta;
	}

	protected abstract query<R extends QueryResultRow = QueryResultRow, T extends Array<unknown> = unknown[]>(
		query: string,
		values?: QueryConfigValues<T>
	): Promise<QueryResult<R>>;

	async queryOne<T>(query: string, options: unknown[], requesterDetails: RequesterDetails): Promise<T> {
		const formattedQuery = questionMarksToOrderedParams(query);
		const meta = this.buildQueryMetadata(requesterDetails);
		const queryMetadata = `--QUERY_METADATA(${JSON.stringify(meta)})\n`;

		const startTime = process.hrtime();
		try {
			const response = await this.query(queryMetadata + formattedQuery, options as QueryConfigValues<unknown>);

			// There should be one and only one row returned
			if (response.rows.length === 0) throw new RsError('NOT_FOUND', 'No results found');
			else if (response.rows.length > 1) throw new RsError('DUPLICATE', 'More than one result found');

			this.logSqlStatement(formattedQuery, options, meta, startTime);

			return response.rows[0] as T;
			// eslint-disable-next-line  @typescript-eslint/no-explicit-any
		} catch (error: any) {
			this.logSqlStatement(formattedQuery, options, meta, startTime);
			if (RsError.isRsError(error)) throw error;

			if (error?.routine === '_bt_check_unique') {
				throw new RsError('DUPLICATE', error.message);
			}
			throw new RsError('DATABASE_ERROR', `${error.message}`);
		}
	}

	async queryOneSchema<T>(
		query: string,
		params: unknown[],
		requesterDetails: RequesterDetails,
		zodSchema: z.ZodSchema<T>
	): Promise<T> {
		const result = await this.queryOne(query, params, requesterDetails);
		try {
			return zodSchema.parse(result);
		} catch (error) {
			if (error instanceof z.ZodError) {
				logger.error('Invalid data returned from database:');
				logger.trace('\n' + JSON.stringify(result, null, 2));
				logger.error('\n' + z.prettifyError(error));
			} else {
				logger.error(error);
			}
			throw new RsError('DATABASE_ERROR', `Invalid data returned from database`);
		}
	}

	async runQuery<T>(query: string, options: unknown[], requesterDetails: RequesterDetails): Promise<T[]> {
		const formattedQuery = questionMarksToOrderedParams(query);
		const meta = this.buildQueryMetadata(requesterDetails);
		const queryMetadata = `--QUERY_METADATA(${JSON.stringify(meta)})\n`;
		const startTime = process.hrtime();
		try {
			const response = await this.query(queryMetadata + formattedQuery, options as QueryConfigValues<unknown>);
			this.logSqlStatement(formattedQuery, options, meta, startTime);

			return response.rows as T[];
			// eslint-disable-next-line  @typescript-eslint/no-explicit-any
		} catch (error: any) {
			this.logSqlStatement(formattedQuery, options, meta, startTime);

			if (error?.routine === '_bt_check_unique') {
				throw new RsError('DUPLICATE', error.message);
			}
			throw new RsError('DATABASE_ERROR', `${error.message}`);
		}
	}

	async runQuerySchema<T>(
		query: string,
		params: unknown[],
		requesterDetails: RequesterDetails,
		zodSchema: z.ZodSchema<T>
	): Promise<T[]> {
		const result = await this.runQuery(query, params, requesterDetails);
		try {
			return z.array(zodSchema).parse(result);
		} catch (error) {
			if (error instanceof z.ZodError) {
				logger.error('Invalid data returned from database:');
				logger.trace('\n' + JSON.stringify(result, null, 2));
				logger.error('\n' + z.prettifyError(error));
			} else {
				logger.error(error);
			}
			throw new RsError('DATABASE_ERROR', `Invalid data returned from database`);
		}
	}

	private logSqlStatement(
		query: string,
		options: unknown[],
		queryMetadata: QueryMetadata,
		startTime: [number, number],
		prefix: string = ''
	) {
		if (logger.level !== 'trace') return;

		const sqlStatement = query.replace(/\$(\d+)/g, (_, num) => {
			const paramIndex = parseInt(num) - 1;
			if (paramIndex >= options.length) return 'INVALID_PARAM_INDEX';
			return toSqlLiteral(options[paramIndex]);
		});

		const formattedSql = sqlFormat(sqlStatement, {
			language: 'postgresql',
			linesBetweenQueries: 2,
			indentStyle: 'standard',
			keywordCase: 'upper',
			useTabs: true,
			tabWidth: 4
		});

		const [seconds, nanoseconds] = process.hrtime(startTime);
		const durationMs = seconds * 1000 + nanoseconds / 1000000;

		let initiator = 'Anonymous';
		const initiatorId = queryMetadata.userId ?? (queryMetadata.adminUserId as number | undefined);
		if (initiatorId) initiator = `User Id (${initiatorId.toString()})`;
		if ('isSystemUser' in queryMetadata && queryMetadata.isSystemUser) initiator = 'SYSTEM';

		logger.trace(`${prefix}query by ${initiator}, Query ->\n${formattedSql}`, {
			durationMs
		});
	}
}
